import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useSeo } from '../lib/useSeo';

interface LegalPageProps {
  title: string;
  path: string;
  intro: React.ReactNode;
  sections: { heading: string; body: React.ReactNode }[];
}

export default function LegalPage({ title, path, intro, sections }: LegalPageProps) {
  useSeo({
    title: `${title} - DAIEN`,
    description: `${title} serwisu daien.pl. Informacje prawne dotyczące korzystania z serwisu i przetwarzania danych osobowych.`,
    path,
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <header className="px-6 md:px-10 lg:px-16 pt-10 pb-6 max-w-4xl mx-auto">
        <Link to="/" className="text-sm text-muted hover:text-text-primary transition-colors inline-flex items-center gap-2">
          ← DAIEN
        </Link>
      </header>
      <article className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="flex items-center gap-4 mb-6 mt-8">
          <span className="w-8 h-px bg-stroke"></span>
          <span className="text-xs text-muted uppercase tracking-[0.3em]">Dokumenty</span>
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light mb-6">{title}</h1>
        <div className="text-muted text-lg mb-12 max-w-2xl">{intro}</div>
        <div className="space-y-10">
          {sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-xl md:text-2xl font-medium mb-3">{s.heading}</h2>
              <div className="text-muted leading-relaxed space-y-3">{s.body}</div>
            </section>
          ))}
        </div>
        <div className="mt-16 pt-6 border-t border-stroke text-xs text-muted space-y-1">
          <p>Ostatnia aktualizacja: 17 maja 2026</p>
          <p>Dawid Stępień · ul. Żelazna 14 lok. 37, 81-150 Gdynia · NIP 9581764814 · REGON 544725271 · <a href="mailto:dawid@daien.pl" className="underline">dawid@daien.pl</a></p>
          <p className="pt-2 flex flex-wrap gap-x-3 gap-y-1">
            <Link to="/polityka-prywatnosci" className="hover:text-text-primary">Polityka prywatności</Link>
            <span>·</span>
            <Link to="/rodo" className="hover:text-text-primary">RODO</Link>
            <span>·</span>
            <Link to="/polityka-cookies" className="hover:text-text-primary">Polityka cookies</Link>
            <span>·</span>
            <Link to="/regulamin" className="hover:text-text-primary">Regulamin</Link>
          </p>
        </div>
      </article>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalPage
      title="Polityka prywatności"
      path="/polityka-prywatnosci"
      intro={
        <p>
          W skrócie: zbieram tylko to, co potrzebne do kontaktu i nie sprzedaję Twoich danych. Pełne szczegóły znajdziesz w{' '}
          <Link to="/rodo" className="underline hover:text-text-primary">klauzuli RODO</Link> i{' '}
          <Link to="/polityka-cookies" className="underline hover:text-text-primary">polityce cookies</Link>.
        </p>
      }
      sections={[
        {
          heading: '1. Administrator danych',
          body: (
            <p>
              Administratorem danych osobowych jest <strong>Dawid Stępień</strong> prowadzący indywidualną działalność gospodarczą pod marką DAIEN, ul. Żelazna 14 lok. 37, 81-150 Gdynia, NIP 9581764814, REGON 544725271. Kontakt: <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a>.
            </p>
          ),
        },
        {
          heading: '2. Jakie dane zbieram',
          body: (
            <ul className="list-disc pl-6 space-y-2">
              <li>Dane z formularzy kontaktowych i lead capture: imię, nazwisko, firma, stanowisko, email, telefon, treść wiadomości.</li>
              <li>Dane z quizu kwalifikacyjnego: branża i odpowiedzi diagnostyczne (anonimowe dopóki nie podasz danych kontaktowych).</li>
              <li>Dane techniczne i cookies analityczne/marketingowe (Google Analytics 4, PostHog, Meta Pixel) - tylko po Twojej zgodzie w bannerze cookies.</li>
            </ul>
          ),
        },
        {
          heading: '3. Cel przetwarzania',
          body: (
            <p>
              Odpowiedź na zapytanie, przygotowanie i wysłanie oferty, kontakt telefoniczny w trybie speed-to-lead, statystyki ruchu (anonimowe) oraz retargeting w reklamach Meta (po zgodzie). Brak masowego marketingu i sprzedaży danych podmiotom trzecim.
            </p>
          ),
        },
        {
          heading: '4. Twoje prawa (RODO)',
          body: (
            <p>
              Masz prawo dostępu, sprostowania, usunięcia, ograniczenia, przeniesienia, sprzeciwu, cofnięcia zgody i skargi do PUODO. Pełna treść klauzuli informacyjnej w{' '}
              <Link to="/rodo" className="underline hover:text-text-primary">RODO</Link>. Wystarczy email na{' '}
              <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a>, odpowiadam w 14 dni.
            </p>
          ),
        },
        {
          heading: '5. Cookies',
          body: (
            <p>
              Strona używa cookies niezbędnych (motyw, preferencje cookies), analitycznych (Google Analytics 4, PostHog) i marketingowych (Meta Pixel). Cookies analityczne i marketingowe ładują się dopiero po Twojej zgodzie w bannerze. Pełna lista cookies w{' '}
              <Link to="/polityka-cookies" className="underline hover:text-text-primary">polityce cookies</Link>.
            </p>
          ),
        },
        {
          heading: '6. Kontakt',
          body: <p>W sprawach związanych z danymi osobowymi: <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a>.</p>,
        },
      ]}
    />
  );
}

export function RodoPage() {
  return (
    <LegalPage
      title="Klauzula informacyjna RODO"
      path="/rodo"
      intro={
        <p>
          Klauzula informacyjna zgodna z art. 13 Rozporządzenia Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. w sprawie ochrony osób fizycznych w związku z przetwarzaniem danych osobowych (RODO).
        </p>
      }
      sections={[
        {
          heading: '1. Administrator danych osobowych',
          body: (
            <div className="space-y-2">
              <p>Administratorem Twoich danych osobowych jest:</p>
              <p>
                <strong>Dawid Stępień</strong><br />
                Indywidualna działalność gospodarcza<br />
                ul. Żelazna 14 lok. 37, 81-150 Gdynia<br />
                NIP: 9581764814 · REGON: 544725271<br />
                Email: <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a><br />
                Strona: <a className="underline" href="https://daien.pl">https://daien.pl</a>
              </p>
            </div>
          ),
        },
        {
          heading: '2. Cele i podstawy prawne przetwarzania',
          body: (
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>art. 6 ust. 1 lit. b RODO</strong> - czynności zmierzające do zawarcia umowy: obsługa formularzy kontaktowych, lead capture form, odpowiedź na zapytanie ofertowe, umawianie spotkań.</li>
              <li><strong>art. 6 ust. 1 lit. f RODO</strong> - prawnie uzasadniony interes administratora: marketing własnych usług DAIEN, analityka anonimowa ruchu na stronie, ulepszanie oferty.</li>
              <li><strong>art. 6 ust. 1 lit. a RODO</strong> - zgoda osoby: cookies marketingowe (Meta Pixel, remarketing) i analityczne (Google Analytics 4, PostHog).</li>
              <li><strong>art. 6 ust. 1 lit. c RODO</strong> - obowiązek prawny ciążący na administratorze: obowiązki podatkowe i księgowe (przechowywanie dokumentów księgowych przez 5 lat).</li>
            </ul>
          ),
        },
        {
          heading: '3. Kategorie przetwarzanych danych',
          body: (
            <ul className="list-disc pl-6 space-y-1">
              <li>Dane identyfikacyjne: imię, nazwisko, nazwa firmy, stanowisko</li>
              <li>Dane kontaktowe: adres email, numer telefonu, adres strony internetowej</li>
              <li>Dane techniczne: adres IP, identyfikator urządzenia, dane przeglądarki (cookies)</li>
              <li>Dane behawioralne: odwiedzane podstrony, odpowiedzi z quizu kwalifikacyjnego, źródło wejścia (utm)</li>
              <li>Dane treści: treść wiadomości w formularzu kontaktowym</li>
            </ul>
          ),
        },
        {
          heading: '4. Odbiorcy danych (podmioty przetwarzające)',
          body: (
            <div className="space-y-2">
              <p>Dane mogą być przekazywane podmiotom świadczącym usługi na rzecz administratora (procesorom):</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Netlify Inc.</strong> (USA) - hosting strony i Netlify Forms</li>
                <li><strong>Google LLC</strong> (USA) - Google Calendar (booking), Google Analytics 4</li>
                <li><strong>Meta Platforms Inc.</strong> (USA) - Meta Pixel (Facebook/Instagram retargeting), po zgodzie</li>
                <li><strong>PostHog Inc.</strong> (host w UE, eu.i.posthog.com) - analityka produktowa i nagrania sesji, po zgodzie</li>
                <li><strong>Anthropic PBC</strong> (USA) - opcjonalne wsparcie AI w obsłudze zapytań</li>
                <li><strong>Trigger.dev</strong> - workflow automation (jeśli używany)</li>
              </ul>
              <p>Wszystkie podmioty działają na podstawie umów powierzenia przetwarzania danych zgodnych z art. 28 RODO.</p>
            </div>
          ),
        },
        {
          heading: '5. Okres przechowywania danych',
          body: (
            <ul className="list-disc pl-6 space-y-1">
              <li>Leady niekonwertowane (kontakt bez umowy): 12 miesięcy od ostatniego kontaktu</li>
              <li>Klienci umowni: czas trwania współpracy + 5 lat (obowiązek księgowy)</li>
              <li>Anonimowe odpowiedzi z quizu (bez danych kontaktowych): 30 dni</li>
              <li>Cookies: zgodnie z polityką cookies (od 24 godzin do 2 lat)</li>
              <li>Dane do marketingu na podstawie zgody: do cofnięcia zgody</li>
            </ul>
          ),
        },
        {
          heading: '6. Transfer danych poza Europejski Obszar Gospodarczy',
          body: (
            <p>
              Część odbiorców (Netlify, Google, Meta, Anthropic) ma siedzibę w USA. Transfer odbywa się na podstawie Standardowych Klauzul Umownych (Standard Contractual Clauses) zatwierdzonych przez Komisję Europejską oraz w ramach Data Privacy Framework (USA - UE).
            </p>
          ),
        },
        {
          heading: '7. Twoje prawa',
          body: (
            <div className="space-y-2">
              <p>W związku z przetwarzaniem danych przysługują Ci następujące prawa:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>prawo dostępu do danych (art. 15 RODO)</li>
                <li>prawo do sprostowania (art. 16 RODO)</li>
                <li>prawo do usunięcia, tzw. "prawo do bycia zapomnianym" (art. 17 RODO)</li>
                <li>prawo do ograniczenia przetwarzania (art. 18 RODO)</li>
                <li>prawo do przenoszenia danych (art. 20 RODO)</li>
                <li>prawo do sprzeciwu wobec przetwarzania (art. 21 RODO)</li>
                <li>prawo do cofnięcia zgody w dowolnym momencie (art. 7 ust. 3 RODO)</li>
                <li>prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych (PUODO), ul. Stawki 2, 00-193 Warszawa</li>
              </ul>
            </div>
          ),
        },
        {
          heading: '8. Automatyczne podejmowanie decyzji',
          body: (
            <p>
              Administrator nie podejmuje wobec Ciebie zautomatyzowanych decyzji wywołujących skutki prawne w rozumieniu art. 22 RODO. Wsparcie AI (np. scoring quizu) ma charakter pomocniczy - finalna decyzja o kontakcie i ofercie należy do administratora.
            </p>
          ),
        },
        {
          heading: '9. Dobrowolność podania danych',
          body: (
            <p>
              Podanie danych jest dobrowolne, ale niezbędne do nawiązania kontaktu i przygotowania oferty. Bez podania danych kontaktowych (email lub telefon) nie ma technicznej możliwości odpowiedzi na zapytanie.
            </p>
          ),
        },
        {
          heading: '10. Kontakt w sprawie danych osobowych',
          body: (
            <p>
              W każdej sprawie dotyczącej Twoich danych osobowych pisz na <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a>. Odpowiadam w terminie do 14 dni (zazwyczaj szybciej).
            </p>
          ),
        },
      ]}
    />
  );
}

export function CookiesPage() {
  return (
    <LegalPage
      title="Polityka cookies"
      path="/polityka-cookies"
      intro={
        <p>
          Strona daien.pl używa plików cookies oraz technologii Local Storage. Poniżej pełna lista wykorzystywanych plików, ich cel, okres przechowywania oraz informacja jak nimi zarządzać.
        </p>
      }
      sections={[
        {
          heading: '1. Czym są cookies',
          body: (
            <p>
              Cookies to małe pliki tekstowe zapisywane w Twojej przeglądarce przez odwiedzane strony. Pozwalają zapamiętać preferencje (np. wybór motywu), mierzyć ruch oraz wyświetlać dopasowane reklamy. Local Storage to podobna technologia, ale z dłuższym okresem przechowywania niż standardowe cookies.
            </p>
          ),
        },
        {
          heading: '2. Cookies niezbędne (zawsze włączone)',
          body: (
            <div className="space-y-2">
              <p>Te cookies są wymagane do działania strony i nie wymagają zgody:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><code>daien-theme</code> (Local Storage) - zapamiętuje preferencję motywu jasny/ciemny. Własny. Bezterminowy.</li>
                <li><code>daien-cookies-consent</code> (Local Storage) - zapis Twojej decyzji w bannerze cookies. Własny. Bezterminowy.</li>
              </ul>
            </div>
          ),
        },
        {
          heading: '3. Cookies analityczne (wymagają zgody)',
          body: (
            <div className="space-y-2">
              <p>Ładowane tylko po akceptacji bannera. Pomagają mierzyć ruch i ulepszać stronę:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><code>_ga</code>, <code>_ga_*</code> - Google Analytics 4 (gtag.js). 2 lata. USA. Identyfikator użytkownika.</li>
                <li><code>_gid</code> - Google Analytics, identyfikator sesji. 24 godziny. USA.</li>
                <li><code>ph_*</code> - PostHog, analityka produktowa i nagrania sesji. Host w UE (eu.i.posthog.com). Do 1 roku.</li>
              </ul>
            </div>
          ),
        },
        {
          heading: '4. Cookies marketingowe (wymagają zgody)',
          body: (
            <div className="space-y-2">
              <p>Ładowane tylko po akceptacji bannera. Używane do retargetingu w reklamach Meta:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><code>_fbp</code> - Meta Pixel, identyfikator przeglądarki dla retargetingu Facebook/Instagram. 90 dni. USA.</li>
                <li><code>_fbc</code> - Meta Pixel, identyfikator kliknięcia w reklamę. 90 dni. USA.</li>
              </ul>
            </div>
          ),
        },
        {
          heading: '5. Jak zarządzać cookies',
          body: (
            <div className="space-y-3">
              <p>
                Możesz w każdej chwili zmienić swoje preferencje klikając "Zmień ustawienia cookies" w stopce strony. Banner pojawi się ponownie.
              </p>
              <p>
                Cookies możesz też usunąć lub zablokować w ustawieniach przeglądarki:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><a className="underline" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a></li>
                <li><a className="underline" href="https://support.apple.com/pl-pl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a className="underline" href="https://support.mozilla.org/pl/kb/usuwanie-ciasteczek-i-danych-stron-firefox" target="_blank" rel="noopener noreferrer">Firefox</a></li>
              </ul>
              <p>
                Opt-out konkretnych usług:{' '}
                <a className="underline" href="https://www.facebook.com/about/privacy/" target="_blank" rel="noopener noreferrer">Meta</a>{' '}·{' '}
                <a className="underline" href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics</a>
              </p>
            </div>
          ),
        },
      ]}
    />
  );
}

export function TermsPage() {
  return (
    <LegalPage
      title="Regulamin"
      path="/regulamin"
      intro={<p>Zasady korzystania z daien.pl i warunki współpracy z DAIEN.</p>}
      sections={[
        {
          heading: '1. Operator serwisu',
          body: (
            <p>
              Operatorem serwisu daien.pl jest <strong>Dawid Stępień</strong> prowadzący indywidualną działalność gospodarczą pod marką DAIEN, ul. Żelazna 14 lok. 37, 81-150 Gdynia, NIP 9581764814, REGON 544725271, email <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a>.
            </p>
          ),
        },
        {
          heading: '2. Charakter serwisu',
          body: (
            <p>
              Serwis ma charakter informacyjny i marketingowy. Treści (case studies, blog, opisy usług, materiały wideo) są chronione prawem autorskim. Kopiowanie do celów komercyjnych wymaga zgody operatora.
            </p>
          ),
        },
        {
          heading: '3. Formularz kontaktowy i lead capture',
          body: (
            <p>
              Wypełnienie formularza kontaktowego lub krótkiego formularza lead capture (imię, nazwisko, telefon) na podstronach niche jest dobrowolne. Dane służą wyłącznie do nawiązania kontaktu telefonicznego w trybie speed-to-lead i przygotowania oferty. Pełne informacje o przetwarzaniu danych w <Link to="/rodo" className="underline">klauzuli RODO</Link>.
            </p>
          ),
        },
        {
          heading: '4. Quiz kwalifikacyjny',
          body: (
            <p>
              Quiz kwalifikacyjny ma charakter diagnostyczny i nie tworzy zobowiązania po żadnej stronie. Wyniki quizu są wskaźnikiem dopasowania, a finalna oferta jest ustalana podczas rozmowy.
            </p>
          ),
        },
        {
          heading: '5. Umawianie spotkań',
          body: (
            <p>
              Spotkania są umawiane przez integrację z Google Calendar. Wybór terminu jest dobrowolny. Spotkanie wstępne jest bezpłatne i nie zobowiązuje do zawarcia umowy.
            </p>
          ),
        },
        {
          heading: '6. Współpraca komercyjna',
          body: (
            <p>
              Każde wdrożenie jest poprzedzone osobną umową w formie pisemnej lub elektronicznej (z podpisem kwalifikowanym albo wymianą skanów). Umowa zawiera szczegółowy zakres prac, harmonogram, cenę i zasady ewentualnych zmian (change requests).
            </p>
          ),
        },
        {
          heading: '7. Reklamacje',
          body: (
            <p>
              Reklamacje dotyczące działania serwisu lub realizacji umowy należy zgłaszać emailem na <a className="underline" href="mailto:dawid@daien.pl">dawid@daien.pl</a>. Rozpatrzenie reklamacji następuje w terminie 14 dni od jej otrzymania.
            </p>
          ),
        },
        {
          heading: '8. Odpowiedzialność i zmiany regulaminu',
          body: (
            <div className="space-y-2">
              <p>
                Operator nie ponosi odpowiedzialności za skutki samodzielnego wdrażania rozwiązań opisanych w blogu lub case studies bez konsultacji. Materiały edukacyjne nie zastępują wdrożenia produkcyjnego.
              </p>
              <p>
                Istotne zmiany regulaminu zostaną ogłoszone na tej stronie z 14-dniowym wyprzedzeniem. Data ostatniej aktualizacji widoczna w stopce dokumentu.
              </p>
            </div>
          ),
        },
      ]}
    />
  );
}
